import { useAssetStore } from './asset'
import { useCrmStore } from './crm'
import { usePlanStore } from './plan'
import { useSessionStore } from './session'
import type { SyncOptions } from './sync'
import type { V4FuneralHome } from '@/apiTypes'
import type Plan from '@/classes/Plan'
import type {
	ConditionalFile,
	DefaultDocumentRule,
	DocumentFile,
	FuneralHome,
	SingleFile,
	ValidState,
} from '@/data/documentRules'
import {
	DocumentRules,
	getCorporateAffiliationRules,
} from '@/data/documentRules'
import { DocumentType } from '@/enums/DocumentType'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import type { StoreDocument } from '@/types'
import { faker } from '@faker-js/faker'
import { captureException } from '@sentry/vue'
import localforageLib from 'localforage'
import { defineStore } from 'pinia'
import { ref, toRaw } from 'vue'

/** IndexedDB for storing plan holder documents */
const localDocuments = localforageLib.createInstance({
	name: 'documents',
})

type DocumentTemplate = Partial<Record<DocumentType, SingleFile>>

/**
 * Pinia store to determine and download the documents on login,
 * These documents are used as a template and will be pulled from
 * by the sessionDocuments store during the Signing Flow
 */
export const useDocumentStore = defineStore('document', () => {
	const funeralHomes = ref<FuneralHome[]>([])
	const states = ref<string[]>([])
	const files = ref<SingleFile[]>([])
	const documentTemplates = ref<DocumentTemplate>()
	const assetStore = useAssetStore()
	const sessionStore = useSessionStore()
	const planHoldersDocuments = ref<StoreDocument[]>([])
	const crmStore = useCrmStore()
	const planStore = usePlanStore()
	const cloudSyncService = useCloudSyncService()

	// This method sets the files for the store, de-duplicated by link.
	// Funeral homes commonly share a state and a corporate affiliation, so the
	// same template would otherwise be queued for download several times a sync.
	const setFiles = (values: SingleFile[]) => {
		const filesByLink = new Map<string, SingleFile>()

		for (const file of values) {
			// A template with no link can never be cached, so it would go missing
			// offline. Skip it rather than throwing, which would abort the download
			// of every other template, but report it so it does not go unnoticed.
			if (!file?.link) {
				captureException(
					new Error(
						`Document rule has no link and cannot be cached: ${file?.name ?? 'unnamed'}`
					)
				)
				continue
			}

			const link = assetStore.getDocumentUrl(file.link)
			if (!filesByLink.has(link)) {
				filesByLink.set(link, { name: file.name, link })
			}
		}

		files.value = Array.from(filesByLink.values())
	}

	// This method sets the funeral homes for the store
	const setFuneralHomes = (values: FuneralHome[]) => {
		funeralHomes.value = values
	}

	// This method sets the states for the store
	const setStates = (values: string[]) => {
		states.value = values
	}

	// This method sets the documents for the session
	const setDocumentTemplates = (values: DocumentTemplate) => {
		documentTemplates.value = values
	}

	// This method sets the funeral homes, states and files for the store
	const initializeStoreData = async () => {
		if (!crmStore.funeralHomes) {
			throw new Error('No funeral homes found')
		}

		setFuneralHomes(
			crmStore.funeralHomes.map((fh: V4FuneralHome) => ({
				id: fh.id,
				name: fh.name,
				state: fh.billingAddress?.stateCode as string,
				corporateAffiliation: fh.corporateAffiliation,
			}))
		)
		setStates(
			Array.from(
				new Set(funeralHomes.value.map((fh: { state: string }) => fh.state))
			)
		)
		setFiles(determineRequiredDocuments())
	}

	// This method determines the required documents for the session, filesList is fetched from server during sync
	const determineRequiredDocuments = () => {
		const filesList: SingleFile[] = []

		// Default
		const defaultDocs = DocumentRules.default as Record<
			DocumentType,
			DocumentFile
		>
		const defaultDocuments: SingleFile[] = []
		for (const documentType of Object.values(defaultDocs)) {
			if ('options' in documentType) {
				defaultDocuments.push(...Object.values(documentType.options))
				continue
			}
			defaultDocuments.push(documentType)
		}

		filesList.push(...defaultDocuments)

		// States
		states.value.forEach((state) => {
			const stateDocuments: SingleFile[] = []
			if (state in DocumentRules.state) {
				const validState = state as ValidState
				const stateDocs = DocumentRules.state[validState] as Record<
					string,
					DocumentFile
				>
				// if it could be multiple files for one type, push all possible links for download
				for (const documentType of Object.values(stateDocs)) {
					if ('options' in documentType) {
						stateDocuments.push(...Object.values(documentType.options))
						continue
					}
					stateDocuments.push(documentType)
				}

				filesList.push(...stateDocuments)
			}
		})

		// Corporate Affiliations
		funeralHomes.value.forEach((funeralHome) => {
			const affiliationDocs = getCorporateAffiliationRules(
				funeralHome.state,
				funeralHome.corporateAffiliation
			)

			if (!affiliationDocs) {
				return
			}

			const fhDocuments: SingleFile[] = []

			// if it could be multiple files for one type, push all possible links for download
			for (const documentType of Object.values(affiliationDocs)) {
				if ('options' in documentType) {
					fhDocuments.push(...Object.values(documentType.options))
					continue
				}
				fhDocuments.push(documentType)
			}

			filesList.push(...fhDocuments)
		})

		return filesList
	}

	// This method gets the documents from the server
	const getDocumentsFromServer = async (
		syncOptions: SyncOptions = { verify: false }
	) => {
		await initializeStoreData()

		return Promise.allSettled(
			files.value.map((file: SingleFile) =>
				assetStore.getAsset(file.link, syncOptions)
			)
		)
	}

	const determineOapForm = (oapCarrier: string) => {
		const oapForms = DocumentRules.default[
			DocumentType.OAPEnrollment
		]! as ConditionalFile
		return oapForms.options[oapCarrier]
	}

	// Method to determine the payment authorization form to use
	const determinePaymentAuthorizationForm = (
		isAllowedToSellSinglePayCc: boolean
	) => {
		const paymentAuthorizationForms = DocumentRules.default[
			DocumentType.PaymentAuthorization
		] as ConditionalFile

		return isAllowedToSellSinglePayCc
			? (paymentAuthorizationForms.options[
					DocumentType.SinglePaymentAuthorization
				] as SingleFile)
			: (paymentAuthorizationForms.options[
					DocumentType.PaymentAuthorization
				] as SingleFile)
	}

	// This method merges the default, state, and funeral home specified documents for the session
	const getDocumentTemplates = (funeralHome: V4FuneralHome) => {
		let filesList: DocumentTemplate = {}
		const defaultDocs = DocumentRules.default as DefaultDocumentRule

		for (const [documentType, documentValue] of Object.entries(defaultDocs)) {
			if (documentType === DocumentType.OAPEnrollment && funeralHome.oap) {
				filesList[DocumentType.OAPEnrollment] = determineOapForm(
					funeralHome.oap.data.carrier
				)
				continue
			}
			if (documentType === DocumentType.PaymentAuthorization) {
				filesList[DocumentType.PaymentAuthorization] =
					determinePaymentAuthorizationForm(
						funeralHome.isAllowedToSellSinglePayCc ?? false
					)
				continue
			}
			filesList[documentType as DocumentType] = documentValue as SingleFile
		}

		if (funeralHome.billingAddress?.stateCode! in DocumentRules.state) {
			const validState = funeralHome.billingAddress?.stateCode as ValidState
			const stateRules = DocumentRules.state[validState] as DefaultDocumentRule

			for (const [documentType, documentValue] of Object.entries(stateRules)) {
				filesList[documentType as DocumentType] = documentValue as SingleFile
			}
		}

		const affiliationRules = getCorporateAffiliationRules(
			funeralHome.billingAddress?.stateCode as string,
			funeralHome.corporateAffiliation
		)

		if (affiliationRules) {
			for (const [documentType, documentValue] of Object.entries(
				affiliationRules
			)) {
				filesList[documentType as DocumentType] = documentValue as SingleFile
			}
		}

		// Resolve every link the same way determineRequiredDocuments does before
		// caching it. getDocumentUrl rewrites S3 links to the API proxy, so without
		// this the signing flow would look templates up under the raw S3 URL while
		// the sync cached them under the proxy URL - a guaranteed miss offline.
		for (const [documentType, documentValue] of Object.entries(filesList)) {
			if (!documentValue?.link) {
				continue
			}

			filesList[documentType as DocumentType] = {
				...documentValue,
				link: assetStore.getDocumentUrl(documentValue.link),
			}
		}

		setDocumentTemplates(filesList)
	}

	const getFilledDocumentTypes = (planHolder: Plan) => {
		// Filler out all the documents that don't have an actual data file
		const filledDocuments = planHolder.documents.filter((item) =>
			planHoldersDocuments.value.some((document) => document.id === item.id)
		)

		return filledDocuments.map((d) => d.type)
	}

	// Used to initialize empty documents for planHolders, it skips planHolders who already have loaded documents.
	const initializeDocumentsForPlanHolders = async () => {
		if (!documentTemplates.value) return
		const activePlans = sessionStore.activePlans
		if (!activePlans) return

		for (const planHolder of activePlans) {
			// find the types of documents that a planHolder have already filled, used when creating the template
			const filledDocumentTypes = getFilledDocumentTypes(planHolder)

			// Keep a copy of the planHolder documents (cloudSync filled documents are already populated at this point)
			const existingPlanHolderDocuments = [...planHolder.documents]
			planHolder.documents = []

			// document should always stay in the same order as the documentTemplates
			for (const [type, docValue] of Object.entries(documentTemplates.value)) {
				if (!docValue || !docValue.link) {
					continue
				}

				// if the planHolder already has this doc type, insert the filled version
				const planHolderFilledDocument = filledDocumentTypes.includes(
					type as DocumentType
				)
				if (planHolderFilledDocument) {
					const filledDocument = existingPlanHolderDocuments.find(
						(document) => document.type === type
					)
					planHolder.documents.push(filledDocument!)
					continue
				}

				// if the planHolder has yet to fill the document, grab the template
				try {
					let blob = await assetStore.getAsset(docValue.link)

					if (blob) {
						const arrayBuffer = await blob.arrayBuffer()

						try {
							const documentId = faker.string.uuid()
							const document = {
								id: documentId,
								meta: {
									type: type as DocumentType,
									name: `${docValue.name}-${planHolder.id}`,
									planId: planHolder.id,
									supplemental: docValue.supplemental ?? false,
								},
								blob: arrayBuffer,
								lastUpdated: new Date().toISOString(),
							}
							planHoldersDocuments.value.push(document)
							planHolder.documents.push({
								id: documentId,
								type: type as DocumentType,
							})
							await localDocuments.setItem(documentId, document)
						} catch (error) {
							captureException(error, {
								extra: {
									message: `Error initializing PDFs for (${planHolder.id})`,
								},
							})
						}
					} else {
						console.warn('No blob returned for document:', type)
					}
				} catch (error) {
					captureException(error, {
						extra: {
							message: `Error getting asset for document: ${type}`,
						},
					})
				}
			}
		}
	}

	// This method loads the documents for plan holders that already have documents.
	const loadDocumentsForExistingPlanHolders = async () => {
		const activePlans = sessionStore.activePlans
		if (!activePlans) return

		planHoldersDocuments.value = []

		for (const planHolder of activePlans) {
			const planHolderDocumentIds = planHolder.documents.map(
				(document) => document.id
			)
			await loadDocumentsForPlanHolder(planHolderDocumentIds)
		}
	}

	// This method loads the documents for the given plan holder.
	const loadDocumentsForPlanHolder = async (documentIds: string[]) => {
		const documents = await Promise.all(
			documentIds.map(async (documentId) => {
				const cloudSyncDocument =
					await cloudSyncService.document.getDocumentById(documentId)
				const localDocument = (await localDocuments.getItem(
					documentId
				)) as StoreDocument

				// compare the CloudSync version with the local version, see which one is newer
				const isCloudSyncDocumentNewer = firstIsNewer(
					cloudSyncDocument,
					localDocument
				)

				if (isCloudSyncDocumentNewer && cloudSyncDocument) {
					await localDocuments.setItem(cloudSyncDocument.id, cloudSyncDocument)
				}

				return isCloudSyncDocumentNewer ? cloudSyncDocument : localDocument
			})
		)

		planHoldersDocuments.value.push(
			...documents.filter<StoreDocument>((item) => item !== null).flat()
		)
	}

	/**
	 * This method fetches the most recent versions of documents given a list of IDs
	 * without touching refs or local forage, used for home dashboard printing
	 */
	const fetchDocumentsForPlanHolder = async (documentIds: string[]) => {
		const documents: Array<StoreDocument | null> = await Promise.all(
			documentIds.map(async (documentId) => {
				const cloudSyncDocument =
					await cloudSyncService.document.getDocumentById(documentId)
				const localDocument = (await localDocuments.getItem(
					documentId
				)) as StoreDocument

				// compare the CloudSync version with the local version, see which one is newer
				const isCloudSyncDocumentNewer = firstIsNewer(
					cloudSyncDocument,
					localDocument
				)

				return isCloudSyncDocumentNewer ? cloudSyncDocument : localDocument
			})
		)

		return documents.filter((d) => !!d)
	}

	const firstIsNewer = (
		a: StoreDocument | null,
		b: StoreDocument | null
	): boolean | null => {
		if (!a?.lastUpdated && !b?.lastUpdated) {
			return null
		}
		if (!a?.lastUpdated) {
			return false
		} else if (!b?.lastUpdated) {
			return true
		} else {
			return Date.parse(a?.lastUpdated) > Date.parse(b?.lastUpdated)
		}
	}

	// This method updates a plan holder's document with the newly synced template
	const useNewTemplateForPlanHolderDocument = async (
		planHolderId: string,
		document: StoreDocument
	) => {
		const planHolder = sessionStore.getPlanHolder(planHolderId)
		if (!planHolder) {
			throw new Error(`Plan holder ${planHolderId} not found`)
		}

		const documentIndex = planHoldersDocuments.value.findIndex(
			(doc) => doc.meta.type === document.meta.type
		)

		if (documentIndex !== -1 && documentTemplates.value) {
			const documentRule =
				documentTemplates.value[document.meta.type as DocumentType]
			let blob

			try {
				blob = await assetStore.getAsset(documentRule!.link, {
					verify: true,
				})
			} catch (error) {
				blob = await assetStore.getAsset(documentRule!.link)

				captureException(error, {
					extra: {
						message: `Could not update to new template from server for plan holder: ${planHolderId}. Falling back to latest template on cache.`,
					},
				})
			}

			try {
				// update indexedDB
				await localDocuments.setItem(document.id, {
					id: document.id,
					blob: await blob!.arrayBuffer(),
					meta: {
						type: document.meta.type,
						name: document.meta.name,
						planId: planHolder.id,
						supplemental: document.meta.supplemental,
					},
					lastUpdated: new Date().toISOString(),
				})
			} catch (error) {
				captureException(error, {
					extra: {
						message: `Error updating local storage data for document: ${document.id}`,
					},
				})
				console.error(error)
			}

			// update store
			planHoldersDocuments.value[documentIndex].blob = await blob!.arrayBuffer()
		}
	}

	// This method updates the document for a plan holder.
	const updatePlanHolderDocument = async (
		blob: ArrayBuffer,
		document: StoreDocument
	) => {
		const documentIndex = planHoldersDocuments.value.findIndex(
			(doc) => doc.id === document.id
		)

		if (documentIndex !== -1) {
			planHoldersDocuments.value[documentIndex].blob = blob
			planHoldersDocuments.value[documentIndex].lastUpdated =
				new Date().toISOString()

			try {
				await localDocuments.setItem(
					document.id,
					toRaw(planHoldersDocuments.value[documentIndex])
				)
				await cloudSyncService.document.registerForUpsert(document.id)
			} catch (error) {
				captureException(error, {
					extra: {
						message: `Error updating local forage data for document: ${document.id}`,
					},
				})
			}
		}
	}

	// This method adds the merged document for a plan holder.
	const addPlanHolderMergedDocuments = async (
		planHolderID: string,
		document: ArrayBuffer
	) => {
		let documentId = ''
		const planHolder = sessionStore.getPlanHolder(planHolderID)
		if (!planHolder) {
			throw new Error(`Plan holder ${planHolderID} not found`)
		}

		const existingMergedDocumentIndex = planHolder.documents.findIndex(
			(doc) => doc.type === DocumentType.MergedDocument
		)

		if (existingMergedDocumentIndex !== -1) {
			documentId = planHolder.documents[existingMergedDocumentIndex].id
			const documentsListIndex = planHoldersDocuments.value.findIndex(
				(doc) => doc.id === documentId
			)

			planHoldersDocuments.value[documentsListIndex].blob = document
		} else {
			documentId = faker.string.uuid()
			planHoldersDocuments.value.push({
				id: documentId,
				meta: {
					type: DocumentType.MergedDocument,
					name: 'Merged Document',
					planId: planHolder.id,
					supplemental: false,
				},
				blob: document,
				lastUpdated: new Date().toISOString(),
			})
			planHolder.documents.push({
				id: documentId,
				type: DocumentType.MergedDocument,
			})
		}

		try {
			await localDocuments.setItem(documentId, {
				id: documentId,
				meta: {
					type: DocumentType.MergedDocument,
					name: 'Merged Document',
					planId: planHolder.id,
					supplemental: false,
				},
				blob: document,
				lastUpdated: new Date().toISOString(),
			})
		} catch (error) {
			captureException(error, {
				extra: {
					message: `Error updating local forage for plan holder: ${planHolderID}`,
				},
			})
		}
	}

	// This method adds the merged packet without payment info for a plan holder
	const addPlanHolderEverplansMergedPdf = async (
		planHolderID: string,
		document: ArrayBuffer
	) => {
		let documentId = ''
		const planHolder = sessionStore.getPlanHolder(planHolderID)
		if (!planHolder) {
			throw new Error(`Plan holder ${planHolderID} not found`)
		}

		const existingEverplansDocumentIndex = planHolder.documents.findIndex(
			(doc) => doc.type === DocumentType.EverplansMergedDocument
		)

		if (existingEverplansDocumentIndex !== -1) {
			documentId = planHolder.documents[existingEverplansDocumentIndex].id
			const documentsListIndex = planHoldersDocuments.value.findIndex(
				(doc) => doc.id === documentId
			)

			planHoldersDocuments.value[documentsListIndex].blob = document
		} else {
			documentId = faker.string.uuid()
			planHoldersDocuments.value.push({
				id: documentId,
				meta: {
					type: DocumentType.EverplansMergedDocument,
					name: 'Everplans Merged Document',
					planId: planHolder.id,
					supplemental: false,
				},
				blob: document,
				lastUpdated: new Date().toISOString(),
			})
			planHolder.documents.push({
				id: documentId,
				type: DocumentType.EverplansMergedDocument,
			})
		}

		try {
			await localDocuments.setItem(documentId, {
				id: documentId,
				meta: {
					type: DocumentType.EverplansMergedDocument,
					name: 'Everplans Merged Document',
					planId: planHolder.id,
					supplemental: false,
				},
				blob: document,
				lastUpdated: new Date().toISOString(),
			})
		} catch (error) {
			captureException(error, {
				extra: {
					message: `Error updating local forage copy of everplans document for plan holder: ${planHolderID}`,
				},
			})
		}
	}

	// This method loads the document templates for the active session.
	const loadDocumentTemplates = async () => {
		const funeralHomeId = sessionStore.activeSession?.FuneralHomeLocationId
		const funeralHome = crmStore.funeralHomes?.find(
			(fh) => fh.id === funeralHomeId
		)

		if (funeralHome) {
			getDocumentTemplates(funeralHome)
		}
	}

	// This method returns the merged document for a plan holder.
	const getPlanHolderMergedDocument = (planHolder: Plan) => {
		const document = planHolder.documents.find(
			(document) => document.type === DocumentType.MergedDocument
		)

		if (!document) {
			throw new Error(`Merged document not found for ${planHolder.id}`)
		}

		return getPlanHolderDocumentById(document.id)
	}

	// This method is used to invalidate the mergedDocument if steps are removed outside the SessionDocument.vue.
	const removePlanHolderMergedDocument = async (planHolderID: string) => {
		const planHolder = sessionStore.getPlanHolder(planHolderID)
		if (!planHolder) {
			throw new Error(`Plan holder ${planHolderID} not found`)
		}

		const mergedDocumentIndex = planHolder.documents.findIndex(
			(document) => document.type === DocumentType.MergedDocument
		)

		if (mergedDocumentIndex === -1) {
			return
		}

		// Remove the document from the plan holder documents list
		const [mergedDocument] = planHolder.documents.splice(mergedDocumentIndex, 1)

		// Remove the document from the plan holders documents store
		planHoldersDocuments.value = planHoldersDocuments.value.filter(
			(planHolderDocument) => planHolderDocument.id !== mergedDocument.id
		)

		await planStore.updatePlan(planHolderID, {
			documents: planHolder.documents,
		})

		try {
			await localDocuments.removeItem(mergedDocument.id)
		} catch (error) {
			captureException(error, {
				extra: {
					message: `Error removing merged document for plan holder: ${planHolderID}`,
				},
			})
		}
	}

	// This method returns the documents for a plan holder.
	const getPlanHolderDocuments = (planHolder: Plan) => {
		if (!planHolder.documents || planHolder.documents.length === 0) {
			return []
		}

		const documentIds = planHolder.documents.map((document) => document.id)

		return planHoldersDocuments.value.filter((document) =>
			documentIds.includes(document.id)
		)
	}

	// This method returns the document for a plan holder by type.
	const getPlanHolderDocumentByType = (
		planHolder: Plan,
		documentType: string
	) => {
		const document = planHolder.documents.find(
			(document) => document.type === documentType
		)

		if (!document) {
			throw new Error(`Document ${documentType} not found for ${planHolder.id}`)
		}

		return planHoldersDocuments.value.find(
			(planHolderDoc) => planHolderDoc.id === document.id
		)
	}

	const getPlanHolderDocumentById = (documentId: string) => {
		return planHoldersDocuments.value.find(
			(document) => document.id === documentId
		)
	}
	const doesDocumentUseOldSchema = (document: any) => {
		return document['planId'] !== undefined
	}

	const migrateDocuments = async () => {
		const oldDocuments: {
			planId: string
			documents: any[]
			documentKey: string
		}[] = []

		await new Promise<void>((resolve) => {
			localDocuments.iterate((document: any, key: string) => {
				if (doesDocumentUseOldSchema(document)) {
					oldDocuments.push({ ...document, documentKey: key })
				}
			}, resolve)
		})

		if (oldDocuments.length === 0) {
			return
		}

		for (const documentList of oldDocuments) {
			let plan: Plan | undefined = undefined

			try {
				plan = await planStore.loadPlan(documentList.planId)

				if (!plan) {
					throw new Error(`Cannot load plan with id: ${documentList.planId}`)
				}
			} catch (error) {
				console.error(
					`Migration Error: Cannot load plan with id: ${documentList.planId}`
				)
				continue
			}

			for (const document of documentList.documents) {
				const newDocumentId = faker.string.uuid()
				const newDocument: StoreDocument = {
					id: newDocumentId,
					meta: {
						type: document.type,
						name: document.name,
						planId: plan.id,
						supplemental: document.supplemental,
					},
					blob: document.blob,
					lastUpdated: new Date().toISOString(),
				}
				await localDocuments.setItem(newDocumentId, newDocument)

				plan.documents.push({
					id: newDocumentId,
					type: document.type,
				})
				await planStore.updatePlan(documentList.planId, {
					documents: plan.documents,
				})
			}

			await localDocuments.removeItem(documentList.documentKey)
		}
	}

	const setPlanHoldersDocuments = (documents: StoreDocument[]) => {
		planHoldersDocuments.value = documents
	}

	async function purge() {
		await localDocuments.clear()
	}

	return {
		files,
		funeralHomes,
		states,
		documentTemplates,
		planHoldersDocuments,
		initializeStoreData,
		getDocumentsFromServer,
		getDocumentTemplates,
		initializeDocumentsForPlanHolders,
		fetchDocumentsForPlanHolder,
		loadDocumentTemplates,
		loadDocumentsForExistingPlanHolders,
		loadDocumentsForPlanHolder,
		updatePlanHolderDocument,
		getPlanHolderDocuments,
		getPlanHolderDocumentByType,
		getPlanHolderDocumentById,
		addPlanHolderMergedDocuments,
		addPlanHolderEverplansMergedPdf,
		getPlanHolderMergedDocument,
		removePlanHolderMergedDocument,
		useNewTemplateForPlanHolderDocument,
		migrateDocuments,
		setPlanHoldersDocuments,
		firstIsNewer,
		purge,
	}
})
